// stores/cluster.js — the converged cluster-roster store for the Cluster page.
//
// Two backend surfaces feed the same shape: the management roster
// (GET /members, full management rows, `api:members.manage`) and the converged
// roster (GET /members/roster, `api:members.read`). refresh() tries the management
// roster first and falls back to the converged one on a 403 — the store never fabricates a
// value either code path doesn't provide (honest null/"unknown").

import { adaptLocation } from "../adapters.js";
import { api } from "../apiClient.js";
import { CONNECTIONS } from "../config.js";
import { reconcileRosterToRegistry } from "../connect.js";
import { createStore } from "@thekrystalship/krystal/lib/store";
import { hostsStore } from "./hosts.js";

const clusterStore = createStore({
  nodes: [],
  // Which member holds each of the cluster's capabilities. A capability belongs to the
  // cluster rather than to any member, so it is not a field on a roster row — a member
  // is a node or an anchor, and being an anchor does not say WHICH capability it holds.
  capabilities: [],
  // The node the roster was read from. A node is never in its own roster, so its member id is held
  // beside the rows, read from the node's own host identity, for the surfaces that name a node by
  // member id (a transfer).
  self: null,
  status: "idle",
  error: null,
  everLoaded: false,
  // Whether the management roster answered, which only somebody holding `api:members.manage` gets.
  managed: false,
  // The member these rows were read FROM. A membership write is addressed with `peerId`, which is
  // an id in that member's own peer table and means nothing anywhere else — so the write goes back
  // to whoever answered. A fact about the read, not a choice about the cluster.
  rosterFrom: null,
});

// Management MemberView row → normalized node. A cluster member is a node or an
// anchor; `kind` carries which, so a surface can tell them apart without
// guessing from whether servers came back.
function fromPeerRow(row) {
  return {
    nodeId: row.memberId,
    kind: row.kind || "node",
    label: row.nickname || row.memberId,
    clientUrl: row.url,
    membership: row.membership || "unknown",
    status: row.status || "unknown",
    latencyMs: row.latencyMs ?? null,
    lastSeen: row.lastSeen || null,
    enabled: row.enabled !== false,
    apiVersion: row.apiVersion || null,
    // Where the member says it is. A member this browser holds no session with has no identity
    // block to read, so the roster is the only carrier a map has for it — an anchor above all.
    location: adaptLocation(row.location),
    peerId: row.id,
  };
}

// Converged ClusterMemberView row → normalized node.
function fromClusterNodeRow(row) {
  return {
    nodeId: row.memberId,
    kind: row.kind || "node",
    label: row.label || row.memberId,
    clientUrl: row.clientUrl,
    membership: row.membership || "unknown",
    status: row.status || "unknown",
    latencyMs: row.latencyMs ?? null,
    lastSeen: null,
    enabled: true,
    apiVersion: null,
    location: adaptLocation(row.location),
    peerId: null,
  };
}

function isForbidden(err) {
  return !!err && (err.code === 403 || err.status === 403);
}

// Read one node's view of the roster: the management peer list, falling back to the
// converged roster on a 403. Resolves { nodes, managed }, `managed` saying which answered.
function fetchRoster(hostId) {
  return api.members(hostId).list()
    .then(rows => ({ nodes: rows.map(fromPeerRow), managed: true }))
    .catch(err => {
      if (!isForbidden(err)) throw err;
      return api.members(hostId).roster().then(rows => ({ nodes: rows.map(fromClusterNodeRow), managed: false }));
    });
}

// One roster read, applied. Every path that obtains a roster lands here, so the node
// set the app drives is updated by the same act that updates what the Cluster page
// shows — somebody removing a peer sees it leave the fan-out on the click, rather
// than at whatever point the discovery timer next happens to fire.
// The assignments are read wherever the roster is, and never gate it: a cluster whose
// capability list could not be read is still a cluster whose members are known, and
// failing the roster over it would lose more than it reports. A failed read keeps what is
// already held — "I could not ask" is not "nobody holds it", and blanking would make the
// page claim the cluster has no DNS anchor.
//
// Called from applyRoster rather than from refresh(), because discovery reaches the roster
// without going through refresh: a browser that boots, discovers the cluster and never
// opens the management panel would otherwise hold members and no assignments, and the page
// that most needs to name the holder is the one that would never have it.
function loadCapabilities(hostId) {
  return api.members(hostId).capabilities()
    .then(rows => {
      if (Array.isArray(rows)) clusterStore.setState(st => ({ ...st, capabilities: rows }));
    })
    .catch(() => {});
}

function applyRoster(hostId, { nodes, managed }) {
  const conn = CONNECTIONS.find(c => c.id === hostId);
  const self = conn ? { nodeId: hostId, clientUrl: conn.url } : null;
  clusterStore.setState(s => ({
    ...s, nodes, self,
    status: "ready", error: null, everLoaded: true, managed, rosterFrom: hostId,
  }));
  // Fired alongside, not awaited: the roster is the answer this returns and a slower second
  // read must not hold it up. The store updates when it lands.
  loadCapabilities(hostId);
  // The node set follows the roster: the entry node's answer names the cluster, and the nodes it
  // names are remembered for the next load.
  return reconcileRosterToRegistry(nodes, { localHostId: hostId });
}

// The member holding `capability`, or null when nobody does or nothing has been read
// yet. Absent and held-by-nobody are different answers and both come back as null here
// on purpose: a surface that needs to tell them apart reads the assignment itself.
clusterStore.holderOf = (capability) => {
  const found = clusterStore.getState().capabilities.find(c => c.capability === capability);
  return found && found.held ? found.memberId : null;
};

clusterStore.refresh = (hostId) => {
  clusterStore.setState(s => ({ ...s, status: "loading", error: null }));
  return fetchRoster(hostId).then(roster => {
    applyRoster(hostId, roster);
    return roster.nodes;
  }).catch(err => {
    clusterStore.setState(s => ({ ...s, status: "error", error: err }));
    throw err;
  });
};

// ---- Cluster discovery --------------------------------------------------
// The node set the SPA drives is the CLUSTER's, not the list of addresses this
// browser happens to have been pointed at. Ask any node we can reach for the
// converged roster and register the peers it names; the connection set grows in
// place, so every surface fans out over them from the next read on.
//
// Runs for everybody — somebody without the management roster resolves the
// same roster through the converged path inside fetchRoster. Nodes are tried in order and the FIRST
// that answers wins: the roster is converged, so any reachable node's view is
// the cluster's view. All nodes failing is not an error state here — an
// unreachable cluster is already surfaced by the connection banner, and a
// roster we could not read must not clear or contradict one we already have.
//
// The connection set FOLLOWS the roster (see reconcileRosterToRegistry): an alive
// + reachable peer carrying both a nodeId and a client URL is registered, and a
// node the roster no longer names is dropped. A peer we cannot verify stays a
// visible ghost on the Cluster page rather than becoming a dead connection; a
// member that is merely unwell keeps its connection, because being unreachable is
// not the same as having left.
// Only a connection whose BACKEND id is reconciled can be asked (api.peers
// requires a concrete id) — the seed starts id-less and GET /hosts fills it in.
// The entry node is asked first; the nodes the panel remembers answer for it when it is down.
const addressable = () => CONNECTIONS.filter(c => c.id)
  .sort((a, b) => (b.via === "entry") - (a.via === "entry"));

clusterStore.discover = () => {
  const conns = addressable();
  const attempt = (i) => {
    if (i >= conns.length) return Promise.resolve(0);
    return Promise.resolve()
      .then(() => fetchRoster(conns[i].id))
      .then(roster => applyRoster(conns[i].id, roster).added)
      .catch(() => attempt(i + 1));
  };
  return attempt(0);
};

// Re-run discovery on a slow cadence so a node federated elsewhere joins this
// browser's fan-out on its own, without anyone opening the Cluster page.
const DISCOVER_MS = 60000;
let _discoverTimer = null;
function startDiscovery() {
  if (_discoverTimer || !CONNECTIONS.length) return;
  const run = () => { clusterStore.discover().catch(() => {}); };
  // Nothing is addressable until the first GET /hosts reconciles an id, so wait
  // for that rather than spending the boot attempt on an empty set.
  if (addressable().length) run();
  else {
    const stop = hostsStore.subscribe(() => {
      if (!addressable().length) return;
      stop();
      run();
    });
  }
  _discoverTimer = setInterval(run, DISCOVER_MS);
}

function stopDiscovery() {
  if (!_discoverTimer) return;
  clearInterval(_discoverTimer);
  _discoverTimer = null;
}

export { clusterStore, startDiscovery, stopDiscovery };
